<template>
  <PsModal
    v-model:show="show"
    header="Настройка цветов"
    size="sm"
  >
    <PsTabs
      v-model="codesType"
      :tabs="tabs"
    >
      <div class="tab-content">
        <TreeSearch
          :items="filteredCodes"
          :showExpanded="coloredOnly"
          :expandOnSearch="true"
          :inputPlaceholder="codesType === 'feeds' ? 'Поиск по лентам' : 'Поиск по кодам'"
          class="tree-search"
        >
          <template #item="{ item }">
            <CodeColorItem
              :item="item"
              :type="codesType"
              :colorInfo="currentColorsMap[item.id]"
              @colorChange="setCodeColor(item.id, $event)"
            />
          </template>
        </TreeSearch>
      </div>
      <template #suffix>
        <PsCheckbox
          v-model:checked="coloredOnly"
          label="Только отмеченные"
        />
      </template>
    </PsTabs>
    <template #footer>
      <PsSubmitGroup
        :submitDisabled="!canSubmit"
        submitLabel="Сохранить"
        @submit="applyChanges"
        @cancel="onClose"
      />
    </template>
  </PsModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import CodeColorItem from './CodeColorItem.vue'
import PsSubmitGroup from '@/ContextTab/components/PsSubmitGroup.vue'
import PsCheckbox from '@/ContextTab/components/UI/PsCheckbox.vue'
import PsModal from '@/ContextTab/components/UI/PsModal.vue'
import PsTabs from '@/ContextTab/components/UI/PsTabs.vue'
import TreeSearch from '@/components/basic/Tree/TreeSearch.vue'
import { useCodesStore } from '@/ContextTab/stores/codes'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import { search } from '@/components/basic/Tree/search'
import type { CodeFilterTypes } from '@/ContextTab/modules/news/codes/types'
import type { AppliedColor } from './ColorPicker.vue'
import type { CodesOfColorsOfDataOfUserProfileGetOut } from '@/api/generated/v1'
import type { ColorIndicatorType } from '@/ContextTab/stores/userProfile'


interface TabItem {
  label: string
  id: CodeFilterTypes // Hmm... Why it matches PsTabs::Tab type?
}

const show = defineModel<boolean>({ required: true })
const isSaving = ref(false)

const tabs: TabItem[] = [
  { label: 'Коды', id: 'non-feeds' as const },
  { label: 'Ленты', id: 'feeds' as const },
]

const codesType = ref(tabs[0].id)
const coloredOnly = ref(false)
const colorsToSave = ref<Record<number, AppliedColor>>({})
const colorIndicationType: ColorIndicatorType = 'codes' // api implements only 'codes' on 2025-06-09

const codesStore = useCodesStore()
const profileStore = useUserProfileStore()


// saved + just selected/removed
const currentColorsMap = computed<Record<number, CodesOfColorsOfDataOfUserProfileGetOut>>(() =>
  Object.fromEntries(
    Object.entries({
      ...profileStore.explicitProfileColors[colorIndicationType],
      ...colorsToSave.value,
    })
      .filter(([_, colorInfo]) => Boolean(colorInfo.color)) // exclude just removed colors
      .map(([id, colorInfo]) =>
        [Number(id), { ...colorInfo, id: Number(id) } as CodesOfColorsOfDataOfUserProfileGetOut],
      ),
  ),
)

const filteredCodes = computed(() => {
  const tree = codesType.value === 'feeds'
    ? codesStore.feedAndPubCodes
    : codesStore.nonFeedAndPubCodes

  if (!coloredOnly.value) {
    return tree
  } else {
    const { tree: filteredTree } = search(
      tree,
      (item) => Boolean(currentColorsMap.value[item.id]?.color),
    )

    return filteredTree
  }
})

const canSubmit = computed(() => Object.values(colorsToSave.value).length > 0 && !isSaving.value)

function setCodeColor(id: number, colorInfo: AppliedColor) {
  colorsToSave.value[id] = colorInfo // including empty colors!
}

async function applyChanges() {
  const colorsPatch = {
    [colorIndicationType]: Object.values(currentColorsMap.value),
  }
  try {
    isSaving.value = true
    await profileStore.updateColors(colorIndicationType, colorsPatch)
  } finally {
    isSaving.value = false
  }
  onClose()
}

function onClose() {
  colorsToSave.value = {}
  show.value = false
  codesType.value = tabs[0].id
}
</script>

<style scoped>
.tab-content {
  height: 450px;

  .tree-search {
    height: 100%;
  }
}
</style>
